import { Component, computed, inject, input, output } from '@angular/core';
import { ChartData, MigrationsCardAbstractDirective } from '../migrations-card-abstract.directive';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { Color, LegendPosition } from '@swimlane/ngx-charts';
import { TranslateService } from '@ngx-translate/core';
import { ButtonStyles } from '../../../button/button.component';
import { CustomColor } from '../../services/migrations-dashboard.service';

@Component({
  selector: 'ziro-migrations-donut-chart',
  templateUrl: 'migrations-donut-chart.component.html',
  styleUrls: ['migrations-donut-chart.component.scss'],
  standalone: false,
})
export class MigrationsDonutChartComponent extends MigrationsCardAbstractDirective {
  private _translateService = inject(TranslateService);

  readonly segmentTooltipKeyword = input.required<string>();
  readonly totalSubtext = input.required<string>();
  readonly colorScheme = input.required<Color>();
  readonly legendCountLabelSingular = input.required<string>();
  readonly legendCountLabelPlural = input.required<string>();
  readonly customColors = input<CustomColor[]>([]);
  footerNote = input<string>('');
  disabledActionLabel = input<string>('');

  readonly dataClick = output<string>();
  readonly errorButtonClick = output<void>();
  disabledActionClick = output<void>();

  isScrollableLegend = computed<boolean>(() => this.data().length > 5);

  ZiroIcons = ZiroIcons;
  ButtonStyles = ButtonStyles;
  LegendPosition = LegendPosition;

  constructor() {
    super();
    this.tooltipText = this.tooltipText.bind(this);
  }

  onCardClick($event: any): void {
    if (this.isError() || this.isDisabled()) {
      return;
    }

    const blockedClasses = ['arc', 'icon-refresh'];
    const cssClasses = ($event.target as HTMLElement).classList;
    const clickedBlockClasses = blockedClasses.some((blockedClass) => cssClasses.contains(blockedClass));
    if (!clickedBlockClasses) {
      this.dataClick.emit('');
    }
  }

  onChartSegmentClick($event: ChartData): void {
    this.dataClick.emit($event.name);
  }

  getTotalCount(): number {
    return this.data().reduce((acc, curr) => acc + curr.value, 0);
  }

  getTotalCountFormatted(): string {
    return this.getTotalCount().toLocaleString('en-US');
  }

  getPercentage(value: number): string {
    return ((value / this.getTotalCount()) * 100).toFixed(2);
  }

  onErrorButtonClick(): void {
    this.errorButtonClick.emit();
  }

  onDisabledActionClick(): void {
    this.disabledActionClick.emit();
  }

  isInertDisabledState(): boolean {
    return this.isDisabled() && !this.isError() && !this.isRefreshing() && !this.disabledActionLabel();
  }

  tooltipText(data: { data: ChartData }): string {
    return this.segmentTooltipKeyword()
      ? `${data.data.label} ${this._translateService.instant(this.segmentTooltipKeyword())} <div><strong>${data.data.value}</strong></div>`
      : `${data.data.label} <div><strong>${data.data.value}</strong></div>`;
  }

  getLegendColor(name: string, index: number): string {
    const customColor = this.customColors()?.find(
      (customColor) => customColor.name.toLowerCase() === name.toLowerCase(),
    )?.value;
    return customColor ? customColor : this.colorScheme().domain[index];
  }
}
